/**
 * @fileoverview Component for a RadioButton-like grid of Cards. Cards are uniquely identified by
 * their value property, and can be grouped into sections by using the Header sub-component. Only
 * one card can be selected at a time across all sections. Supports 3-column and 4-column variants.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */

import {SubHeader} from '/src/com/yext/listingslaunchstorm/js/components/common/subheader';
import {Card} from '/ui/components/card/card';
import {ClickableDiv} from '/ui/components/clickable/clickablediv';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/selectablecardgrid.module.scss';

const gridStyles = {
  1: styles.grid1,
  2: styles.grid2,
  3: styles.grid3,
  4: styles.grid4,
  5: styles.grid5,
  6: styles.grid6,
};

/**
 * A grid-layout container for the selectable cards. Only cards/headers should be direct children of
 * an instance of this component. A size can be specified to render a {size}xN grid.
 *
 * @template Value GridCard.props.value can be either a string or an object.
 * @param {{
 *   size: (number | undefined),
 *   onSelected: function(Value),
 *   isSelected: function(Value),
 *   interactive?: boolean,
 *   children?: ?,
 * }} props
 * @return {React.ReactNode}
 */
export function SelectableCardGrid({
  size,
  onSelected,
  isSelected,
  interactive,
  children,
}) {
  if (!size || size < 1 || size > 6) {
    throw new Error('SelectableCardGrid only support grids between 1 and 5 columns.');
  }

  // `data-pendo` must be used directly instead of pendoId as the Clickable components pass through
  // their props as-is (meaning no internal conversion of `pendoId` => `data-pendo`).
  const SelectableCard = (child, index) => {
    // Hack to prevent accidental events without using stop propagation on every child element
    // click event (mainly on the verification step for mobile). In other cases, this should have no
    // effect since selecting the already selected card is effectively a noop.
    const selected = isSelected(child.props.value);

    const getCard = () => (
      child.props.isDisabled
        ? <Card
          taskFlowStyle={true}
          isDisabled={true}
          data-pendo={child.props.pendoId}
        >
          {child}
        </Card>
        : <ClickableDiv
          data-pendo={child.props.pendoId}
          className={styles.card}
          onClick={() => !selected && onSelected(child.props.value)}>
          <Card
            selected={selected}
            taskFlowStyle={true}
            hasNestedInteractiveEl={interactive}
          >
            {child}
          </Card>
        </ClickableDiv>
    );

    return (
      <li key={`${child.props.value}-${index}`}>
        {getCard()}
      </li>);
  };

  // Group the children into tuples of [header, cards]. In the case of no specified header, the
  // card elements will be placed into a tuple with an undefined header (which is not rendered).
  const groupedChildren = React.Children.toArray(children).reduce((acc, child) => {
    if (child.type !== GridCard) {
      acc.push([child, []]);
    } else {
      const [header, cards] = (acc.length === 0) ? [undefined, []] : acc.pop();
      cards.push(child);

      acc.push([header, cards]);
    }

    return acc;
  }, []);

  return groupedChildren.map(([header, cards], index) => (
    <section
      key={`section-${index}`}
      className={styles.section}>
      {header}
      <ul className={gridStyles[size]}>
        {cards.map(SelectableCard)}
      </ul>
    </section>
  ));
}

/**
 * A header with a title and optional description to segment a card layout into multiple grids.
 * This component should have no children (eg, headers go between card nodes not surrounding).
 *
 * @param {{
 *   title: string,
 *   description: (string | undefined),
 * }} props
 * @return {React.ReactNode}
 */
function GridHeader({
  title,
  description,
}) {
  return (
    <div className={styles.header}>
      <SubHeader mainText={title} extraText={description}/>
    </div>
  );
}

/**
 * An individual selectable card surrounding arbitrary content. A selectable card behaves similar to
 * a radio input button, where the `value` identifies the individual input (and should be unique).
 *
 * @param {{
 *   value: (string | Object),
 *   pendoId: (string | undefined),
 *   isDisabled: (boolean | undefined),
 *   children: (React.ReactNode | undefined),
 * }} props
 * @return {React.ReactNode}
 */
function GridCard({
  value,
  pendoId,
  isDisabled,
  children,
}) {
  return (
    <Card.Body>
      <div className={isDisabled ? styles.disabledContent : styles.content}>
        {children}
      </div>
    </Card.Body>
  );
}

SelectableCardGrid.Header = GridHeader;
SelectableCardGrid.Card = GridCard;
